import { useEffect, useState } from "react";
import { Filters } from "consts/transactions";
import { WebRoutes } from "consts/enpoints/web";
import {
    deletePspKycSetting,
    getPspKycSettingById
} from "features/pspKycSettings/api";
import { resetPspKycSettings } from "features/pspKycSettings/slice";
import type { PspKycSetting } from "features/pspKycSettings/types";
import { useAppDispatch } from "hooks/store";
import { useNavigate, useParams } from "infrastructure/router";
import type { ResourceResponse } from "types";

export default function useKycSettingDetail() {
    const params = useParams();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const settingId = params[Filters.pspKycSettingId];

    const [setting, setSetting] = useState<PspKycSetting | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    useEffect(() => {
        setSetting(null);
        setError(null);

        if (!settingId) {
            return;
        }

        const controller = new AbortController();
        setIsLoading(true);

        getPspKycSettingById(settingId)
            .then(async response => {
                if (!response.ok) {
                    throw new Error(`Request failed with status ${response.status}`);
                }
                const json: ResourceResponse<PspKycSetting> = await response.json();
                if (!controller.signal.aborted) {
                    setSetting(json.data);
                }
            })
            .catch(err => {
                if (!controller.signal.aborted) {
                    setError(err?.message ?? 'Failed to load PSP KYC setting');
                }
            })
            .finally(() => {
                if (!controller.signal.aborted) {
                    setIsLoading(false);
                }
            });

        return () => controller.abort();
    }, [settingId]);

    const handleDelete = async () => {
        if (!settingId || isDeleting) {
            return;
        }

        setIsDeleting(true);
        setError(null);

        try {
            const response = await deletePspKycSetting(settingId);
            const json = await response.json().catch(() => ({} as Record<string, unknown>));
            const success = (json as { success?: boolean })?.success;
            const code = (json as { code?: number })?.code;
            const message = (json as { message?: string })?.message;
            const isErrorEnvelope = success === false
                || (typeof code === 'number' && code >= 400);

            if (!response.ok || isErrorEnvelope) {
                throw new Error(message ?? `Request failed with status ${response.status}`);
            }
            dispatch(resetPspKycSettings());
            navigate(WebRoutes.KYCSettings.PSPKYCSettings.Index);
        } catch (err: any) {
            setError(err?.message ?? 'Failed to delete PSP KYC setting');
        } finally {
            setIsDeleting(false);
        }
    };

    return {
        settingId,
        setting,
        isLoading,
        isDeleting,
        error,
        handleDelete
    };
}
